import ky from 'ky';

export const CLIENT_ENDPOINT = process.env.NEXT_PUBLIC_CLIENT_ENDPOINT || 'https://admin.wannit-edu.com'
export const SERVER_ENDPOINT = process.env.NEXT_PUBLIC_SERVER_ENDPOINT || 'https://api.wannit-edu.com'
// export const SERVER_ENDPOINT = 'http://localhost:8000'
export const STATIC_ENDPOINT = process.env.NEXT_PUBLIC_STATIC_ENDPOINT || 'https://static.wannit-edu.com'

export const apiInstance = ky.create({
  prefixUrl: SERVER_ENDPOINT,
  credentials: 'include',
  timeout: 120000,
  retry: { limit: 3, delay: attemptCount => 3 / 2 ** attemptCount * 1000 }
})

export const API_ENDPOINT = {
  account: {
    checkUserUrl: `account/check`,
    idLogin: async (id: string, password: string) => {
      const json = { id, password }
      const result = await apiInstance.post(`account/login/id`, { json });
      const data = await result.json() as any;
      return data
    },
    logout: async () => await apiInstance.post(`account/logout`),
    getUsers: async () => {
      const result = await apiInstance.get(`account/users`)
      const data = await result.json() as any;
      return data
    },
    checkUser: async () => {
      const result = await apiInstance.get(`account/check`)
      const data = await result.json() as any;
      return data
    },
  },
  students: {
    getStudents: async () => {
      const result = await apiInstance.get(`students`)
      const data = await result.json() as any;
      return data
    },
    getStudentById: async (id: number) => {
      const result = await apiInstance.get(`students/${id}`)
      const data = await result.json() as any;
      return data
    },
    addStudent: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`students`, { json })
      const data = await result.json() as any;
      return data
    },
    updateStudent: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`students/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteStudent: async (id: number) => {
      const result = await apiInstance.delete(`students/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  teachers: {
    getTeachers: async () => {
      const result = await apiInstance.get(`teachers`)
      const data = await result.json() as any;
      return data
    },
    getTeacherById: async (id: number) => {
      const result = await apiInstance.get(`teachers/${id}`)
      const data = await result.json() as any;
      return data
    },
    addTeacher: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`teachers`, { json })
      const data = await result.json() as any;
      return data
    },
    updateTeacher: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`teachers/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteTeacher: async (id: number) => {
      const result = await apiInstance.delete(`teachers/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  assignments: {
    getAssignments: async () => {
      const result = await apiInstance.get(`assignments`)
      const data = await result.json() as any;
      return data
    },
    assignStudent: async (studentId: number, teacherId: number) => {
      const json = { studentId, teacherId }
      const result = await apiInstance.post(`assignments`, { json })
      const data = await result.json() as any;
      return data
    },
    unassignStudent: async (studentId: number, teacherId: number) => {
      const json = { studentId, teacherId }
      const result = await apiInstance.delete(`assignments`, { json })
      const data = await result.json() as any;
      return data
    }
  },
  classes: {
    getClasses: async () => {
      const result = await apiInstance.get(`classes`)
      const data = await result.json() as any;
      return data
    },
    getAllClasses: async () => {
      const result = await apiInstance.get(`classes?all=true`)
      const data = await result.json() as any;
      return data
    },
    addClass: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`classes`, { json })
      const data = await result.json() as any;
      return data
    },
    updateClass: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`classes/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteClass: async (id: number) => {
      const result = await apiInstance.delete(`classes/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  issues: {
    getIssues: async (params?: { signalLevels?: string[]; statuses?: string[]; classIds?: string[]; teacherIds?: string[] }) => {
      const sp = new URLSearchParams()
      if (params?.signalLevels?.length) sp.set('signalLevels', params.signalLevels.join(','))
      if (params?.statuses?.length) sp.set('statuses', params.statuses.join(','))
      if (params?.classIds?.length) sp.set('classIds', params.classIds.join(','))
      if (params?.teacherIds?.length) sp.set('teacherIds', params.teacherIds.join(','))
      const query = sp.toString()
      const result = await apiInstance.get(`issues${query ? `?${query}` : ''}`)
      const data = await result.json() as any;
      return data
    },
    getIssueById: async (id: number) => {
      const result = await apiInstance.get(`issues/${id}`)
      const data = await result.json() as any;
      return data
    },
    addIssue: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`issues`, { json })
      const data = await result.json() as any;
      return data
    },
    updateIssue: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`issues/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteIssue: async (id: number) => {
      const result = await apiInstance.delete(`issues/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  newStudents: {
    getNewStudents: async (params?: { statuses?: string[]; classIds?: string[]; teacherIds?: string[] }) => {
      const sp = new URLSearchParams()
      if (params?.statuses?.length) sp.set('statuses', params.statuses.join(','))
      if (params?.classIds?.length) sp.set('classIds', params.classIds.join(','))
      if (params?.teacherIds?.length) sp.set('teacherIds', params.teacherIds.join(','))
      const query = sp.toString()
      const result = await apiInstance.get(`new-students${query ? `?${query}` : ''}`)
      const data = await result.json() as any;
      return data
    },
    getNewStudentById: async (id: number) => {
      const result = await apiInstance.get(`new-students/${id}`)
      const data = await result.json() as any;
      return data
    },
    getNewStudentByStudentId: async (studentId: number) => {
      const result = await apiInstance.get(`new-students/by-student/${studentId}`)
      const data = await result.json() as any;
      return data
    },
    addNewStudent: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`new-students`, { json })
      const data = await result.json() as any;
      return data
    },
    updateNewStudent: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`new-students/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteNewStudent: async (id: number) => {
      const result = await apiInstance.delete(`new-students/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  withdrawnStudents: {
    getWithdrawnStudents: async (params?: { statuses?: string[]; categories?: string[]; classIds?: string[] }) => {
      const sp = new URLSearchParams()
      if (params?.statuses?.length) sp.set('statuses', params.statuses.join(','))
      if (params?.categories?.length) sp.set('categories', params.categories.join(','))
      if (params?.classIds?.length) sp.set('classIds', params.classIds.join(','))
      const query = sp.toString()
      const result = await apiInstance.get(`withdrawn-students${query ? `?${query}` : ''}`)
      const data = await result.json() as any;
      return data
    },
    getWithdrawnStudentById: async (id: number) => {
      const result = await apiInstance.get(`withdrawn-students/${id}`)
      const data = await result.json() as any;
      return data
    },
    getWithdrawnStudentByStudentId: async (studentId: number) => {
      const result = await apiInstance.get(`withdrawn-students/by-student/${studentId}`)
      const data = await result.json() as any;
      return data
    },
    addWithdrawnStudent: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`withdrawn-students`, { json })
      const data = await result.json() as any;
      return data
    },
    updateWithdrawnStudent: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`withdrawn-students/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteWithdrawnStudent: async (id: number) => {
      const result = await apiInstance.delete(`withdrawn-students/${id}`)
      const data = await result.json() as any;
      return data
    }
  },
  handovers: {
    getHandovers: async (params?: { classIds?: string[] }) => {
      const sp = new URLSearchParams()
      if (params?.classIds?.length) sp.set('classIds', params.classIds.join(','))
      const query = sp.toString()
      const result = await apiInstance.get(`handovers${query ? `?${query}` : ''}`)
      const data = await result.json() as any;
      return data
    },
    getHandoverById: async (id: number) => {
      const result = await apiInstance.get(`handovers/${id}`)
      const data = await result.json() as any;
      return data
    },
    addHandover: async (body: any) => {
      const json = { ...body }
      const result = await apiInstance.post(`handovers`, { json })
      const data = await result.json() as any;
      return data
    },
    updateHandover: async (id: number, body: any) => {
      const json = { ...body }
      const result = await apiInstance.put(`handovers/${id}`, { json })
      const data = await result.json() as any;
      return data
    },
    deleteHandover: async (id: number) => {
      const result = await apiInstance.delete(`handovers/${id}`)
      const data = await result.json() as any;
      return data
    }
  }
}
